import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Provisioner = {
  id: string;
  address: string;
};

type NodeSelectionContextType = {
  selectedNode: Provisioner | null;
  setSelectedNode: (node: Provisioner | null) => void;
};

const LOCAL_STORAGE_KEY = 'selectedNode';

const NodeSelectionContext = createContext<NodeSelectionContextType | undefined>(undefined);

export function NodeSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedNode, setSelectedNode] = useState<Provisioner | null>(null);

  useEffect(() => {
    const savedNode = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedNode) {
      try {
        setSelectedNode(JSON.parse(savedNode));
      } catch (error) {
        console.error("Error parsing saved node:", error);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  }, []);

  const handleSetSelectedNode = (node: Provisioner | null) => {
    if (node === null) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setSelectedNode(null);
      return;
    }

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(node));
    setSelectedNode(node);
  };

  return (
    <NodeSelectionContext.Provider 
      value={{ 
        selectedNode, 
        setSelectedNode: handleSetSelectedNode 
      }}
    >
      {children}
    </NodeSelectionContext.Provider>
  );
}

export function useNodeSelection() {
  const context = useContext(NodeSelectionContext);
  if (context === undefined) {
    throw new Error("useNodeSelection must be used within a NodeSelectionProvider");
  }
  return context;
}